import { create } from "zustand";
import { canEdit, useAuthStore } from "../auth/store";
import { aiAccountId } from "../ai/account";
import { subscriptionEditingBlocked } from "../billing/subscription-access";
import { useProjectStore } from "../projects/store";
import { useSystemStore } from "../store";
import { useFeatureFlags } from "../feature-flags";
import { t, useI18n, type TranslationKey } from "../i18n";
import { useAutoSizeVdropTarget } from "../settings/auto-size-vdrop-target";
import type { Issue, System } from "../types";
import { assistantIssueReference, assistantSystemFingerprint } from "./context";
import { parseAssistantPayload } from "./validation";
import { prepareAssistantProposal } from "./proposal";
import { sameAssistantSimulation, snapshotAssistantSimulation } from "./simulation";
import { isAssistantProgress, type AssistantProgress } from "./progress";
import { isAssistantClientError } from "./client-error";
import { cancelAssistant, getAssistantUsage, quoteAssistant, sendAssistant, resumeAssistant, sendAssistantFeedback, type AssistantFailure, type AssistantFeedback, type AssistantQuote, type AssistantUsage, type SendResponse } from "./client";
import { ASSISTANT_MAX_PROMPT_CHARS, ASSISTANT_MAX_HISTORY_TURNS, type AssistantPayload, type AssistantReply } from "./types";

export type AssistantTurn = {
  id: string; role: "user" | "assistant"; content: string; reply?: AssistantReply;
  requestId: string; charged?: number; applied?: boolean; dismissed?: boolean; feedback?: AssistantFeedback;
  applicationError?: "stale" | "readonly" | "failed";
  stopped?: boolean;
  steered?: boolean;
};
export type AssistantQueuedMessage = { id: string; prompt: string; issue: Issue | null };
export type AssistantAppliedEdit = { id: string; identity: string; before: System; after: System };
// The exact history entry is the authority for the assistant's local Undo.
// Matching drawings alone must not undo a later, unrelated operation.
const appliedUndoSnapshots = new WeakMap<AssistantAppliedEdit, { system: System }>();
// Issue actions may seed different wording, but never choose request capability.
type DraftInput = { prompt?: string; mode?: "ask" | "edit"; issue?: Issue; source?: string };
type SessionData = { conversationId: string; messages: AssistantTurn[]; draft: string; issue: Issue | null; queuedMessages: AssistantQueuedMessage[]; queuePaused: boolean };
const blank = (): SessionData => ({ conversationId: crypto.randomUUID(), messages: [], draft: "", issue: null, queuedMessages: [], queuePaused: false });
const KEY = "crimp:assistant-session:v1";
const sessionKey = (identity: string) => `${KEY}:${encodeURIComponent(identity)}`;
const SIGN_IN_KEY = "crimp:assistant-sign-in:v1";
const SIGN_IN_MAX_AGE_MS = 30 * 60_000;

export function clearAssistantSignIn(): void {
  try { sessionStorage.removeItem(SIGN_IN_KEY); } catch { /* Tab storage may be unavailable. */ }
}

/** Mark an explicit guest sign-in so returning can reopen chat and retain its draft. */
export function prepareAssistantSignIn(): void {
  clearAssistantSignIn();
  useAssistantStore.getState().syncIdentity();
  const state = useAssistantStore.getState();
  if (aiAccountId(useAuthStore.getState().user) || !state.identity?.startsWith("guest:") ||
      state.messages.length || state.submitted) return;
  persist();
  try { sessionStorage.setItem(SIGN_IN_KEY, JSON.stringify({ identity: state.identity, expiresAt: Date.now() + SIGN_IN_MAX_AGE_MS })); }
  catch { /* The in-memory draft still survives in-page sign-in. */ }
}

function takeGuestSignInDraft(identity: string): SessionData | null {
  if (identity.startsWith("guest:")) return null;
  try {
    const raw = sessionStorage.getItem(SIGN_IN_KEY);
    clearAssistantSignIn();
    if (!raw || raw.length > 1000) return null;
    const marker = JSON.parse(raw);
    const guestIdentity = `guest:${identity.slice(identity.indexOf(":") + 1)}`;
    if (marker?.identity !== guestIdentity || typeof marker.expiresAt !== "number" ||
        marker.expiresAt <= Date.now() || marker.expiresAt > Date.now() + SIGN_IN_MAX_AGE_MS) return null;
    const data = saved(guestIdentity);
    return data && !data.pending && data.messages.length === 0 ? data : null;
  } catch { return null; }
}

/** Restore suspended/reloaded tabs, scoped to their account and drawing.
 * Deliberately keep sessionStorage: sharing pending edits through localStorage
 * would let a second tab automatically apply the first tab's request.
 * No request bodies go to analytics. */
function saved(key: string): (SessionData & { pending?: AssistantPayload; pendingIssue?: Issue | null; progress?: AssistantProgress; open?: boolean; stopRequested?: boolean }) | null {
  try {
    // Retain submitted requests across project/account switches. The previous
    // single-record format is read only for the identity that originally saved it.
    const raw = JSON.parse(sessionStorage.getItem(sessionKey(key)) ?? sessionStorage.getItem(KEY) ?? "null");
    if (raw?.key !== key || !raw.data || !Array.isArray(raw.data.messages) || typeof raw.data.draft !== "string" || typeof raw.data.conversationId !== "string") return null;
    // Ignore a legacy draft's selected mode. A submitted payload keeps its own
    // mode so retrying it cannot create a second charge or a different request.
    // Keep one extra slot for a failed active prompt restored beside a full
    // queue, so reloading cannot silently discard the last queued message.
    const queuedMessages = Array.isArray(raw.data.queuedMessages) ? raw.data.queuedMessages.filter((item: AssistantQueuedMessage) => item && typeof item.id === "string" && typeof item.prompt === "string" && item.prompt.trim() && item.prompt.length <= ASSISTANT_MAX_PROMPT_CHARS).slice(0, 21) : [];
    const preparing = parseAssistantPayload(raw.preparing);
    if (preparing) queuedMessages.unshift({ id: preparing.requestId, prompt: preparing.prompt, issue: raw.pendingIssue ?? null });
    const pending = parseAssistantPayload(raw.pending) ?? undefined;
    const legacySubmittedDraft = pending && !Array.isArray(raw.data.queuedMessages) && raw.data.draft.trim() === pending.prompt;
    return { ...(typeof raw.open === "boolean" ? { open: raw.open } : {}), stopRequested: raw.stopRequested === true, progress: isAssistantProgress(raw.progress) ? raw.progress : undefined, conversationId: raw.data.conversationId, issue: legacySubmittedDraft ? null : raw.data.issue ?? null, pending, pendingIssue: raw.pendingIssue ?? (legacySubmittedDraft ? raw.data.issue : null), draft: legacySubmittedDraft ? "" : raw.data.draft.slice(0, ASSISTANT_MAX_PROMPT_CHARS), messages: raw.data.messages.slice(-32), queuedMessages, queuePaused: raw.data.queuePaused === true || !!preparing };
  } catch { return null; }
}
function persist() {
  const s = useAssistantStore.getState();
  if (!s.identity) return;
  const data: SessionData = { conversationId: s.conversationId, messages: s.messages.slice(-32), draft: s.draft, issue: s.issue, queuedMessages: s.queuedMessages, queuePaused: s.queuePaused };
  try {
    sessionStorage.setItem(sessionKey(s.identity), JSON.stringify({ key: s.identity, data, open: s.open, pending: s.submitted ? s.payload : null, preparing: !s.submitted ? s.payload : null, pendingIssue: s.pendingIssue, stopRequested: s.stopRequested, ...(s.submitted && s.progress ? { progress: s.progress } : {}) }));
    const legacy = sessionStorage.getItem(KEY);
    if (legacy && JSON.parse(legacy)?.key === s.identity) sessionStorage.removeItem(KEY);
  } catch { /* memory still works */ }
}
export function assistantIdentity(): string {
  const auth = useAuthStore.getState();
  const project = auth.currentProjectId ?? useProjectStore.getState().activeLocalProjectId ?? "canvas";
  return `${aiAccountId(auth.user) ?? "guest"}:${project}`;
}
export function assistantMayEdit(): boolean {
  const auth = useAuthStore.getState();
  const local = useProjectStore.getState();
  return useAssistantStore.getState().editingAllowed &&
    !auth.isPublicView && (auth.currentRole === null || canEdit(auth.currentRole)) &&
    local.saveStatus !== "conflict" && local.saveStatus !== "local-error" &&
    !subscriptionEditingBlocked();
}
/** A quick reply belongs only to the current, completed question. */
export function assistantCanReplyTo(turnId: string): boolean {
  const state = useAssistantStore.getState();
  const latest = state.messages.at(-1);
  return useFeatureFlags.getState().flags.editorAssistant && !!aiAccountId(useAuthStore.getState().user) &&
    state.identity !== null && state.identity === assistantIdentity() && state.phase === "idle" &&
    state.draft.length === 0 && !state.issue && !state.incoming && !state.submitted && !state.payload && !state.quote && !state.queuedMessages.length && !state.stopping &&
    latest?.id === turnId && latest.role === "assistant" &&
    (latest.reply?.kind === "clarification" || latest.reply?.kind === "answer") && !latest.reply.proposal;
}
export function assistantCanChooseForMe(turnId: string): boolean {
  const reply = useAssistantStore.getState().messages.at(-1)?.reply;
  return assistantCanReplyTo(turnId) && reply?.kind === "clarification" && reply.canChooseForMe === true;
}
export function assistantCanUndoAppliedEdit(turnId: string): boolean {
  const { appliedEdit } = useAssistantStore.getState();
  const editor = useSystemStore.getState();
  return !!appliedEdit && appliedEdit.id === turnId && appliedEdit.identity === assistantIdentity() &&
    assistantMayEdit() && editor.system === appliedEdit.after &&
    editor.past.length > 0 && editor.past[editor.past.length - 1] === appliedUndoSnapshots.get(appliedEdit);
}
/** Recognize the canvas Undo without changing historical turn metadata. */
export function assistantAppliedEditWasUndone(turnId: string): boolean {
  const { appliedEdit, identity } = useAssistantStore.getState();
  return !!appliedEdit && appliedEdit.id === turnId && appliedEdit.identity === identity &&
    identity === assistantIdentity() && useSystemStore.getState().system === appliedUndoSnapshots.get(appliedEdit)?.system;
}

let epoch = 0;
let balanceRevision = 0;
let usageRequest = 0;
let activeTransport: { epoch: number; controller: AbortController; recovery: boolean; startedAt: number } | null = null;
function startTransport(id: number, recovery: boolean): AbortSignal {
  activeTransport?.controller.abort();
  const controller = new AbortController();
  activeTransport = { epoch: id, controller, recovery, startedAt: Date.now() };
  return controller.signal;
}
function stopTransport(id?: number): void {
  if (id !== undefined && activeTransport?.epoch !== id) return;
  activeTransport?.controller.abort();
  activeTransport = null;
}

/** Both the first stream and recovery use the same exactly-once handoff. */
function receiveAssistantReply(payload: AssistantPayload, result: Extract<SendResponse, { ok: true }>): void {
  const state = useAssistantStore.getState();
  const turnId = `${payload.requestId}:assistant`;
  const alreadyReceived = state.messages.some(turn => turn.id === turnId);
  const messages: AssistantTurn[] = alreadyReceived ? state.messages : [...state.messages,
    { id: `${payload.requestId}:user`, role: "user", content: payload.prompt, requestId: payload.requestId },
    { id: turnId, role: "assistant", content: result.reply.message, reply: result.reply, requestId: payload.requestId, charged: result.charged,
      ...(result.reply.proposal && payload.mode !== "edit" ? { applicationError: "readonly" as const } : {}) },
  ];
  useAssistantStore.setState({ messages, phase: "idle", progress: null, quote: null, payload: null, pendingIssue: null, submitted: false, error: null, remaining: result.remaining });
  persist();
  if (!alreadyReceived && payload.mode === "edit" && result.reply.proposal && !useAssistantStore.getState().apply(turnId)) {
    useAssistantStore.setState({ queuePaused: true }); persist(); return;
  }
  drainQueue(state.identity);
}

/** Only explicitly queued messages advance; a typed draft is never sent here. */
function drainQueue(identity: string | null): void {
  queueMicrotask(() => {
    const current = useAssistantStore.getState();
    if (current.identity !== identity || identity !== assistantIdentity() || current.phase !== "idle" || current.submitted || current.stopRequested || current.stopping || current.queuePaused || current.error || !current.queuedMessages.length || !useFeatureFlags.getState().flags.editorAssistant) return;
    if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
    void current.sendQueuedMessage(current.queuedMessages[0].id);
  });
}
/** Put a refused/failed snapshot back without replacing a newer follow-up. */
function restorePendingPrompt(payload: AssistantPayload): Partial<AssistantState> {
  const state = useAssistantStore.getState();
  if (!state.draft.trim()) return { draft: payload.prompt, issue: state.pendingIssue };
  return { queuedMessages: [{ id: payload.requestId, prompt: payload.prompt, issue: state.pendingIssue }, ...state.queuedMessages] };
}
function receiveStopped(payload: AssistantPayload, remaining?: number, charged?: number): void {
  useAssistantStore.setState(state => ({ messages: state.messages.some(turn => turn.id === `${payload.requestId}:user`) ? state.messages : [...state.messages, { id: `${payload.requestId}:user`, role: "user", content: payload.prompt, requestId: payload.requestId, stopped: true, ...(charged ? { charged } : {}) }], phase: "idle", progress: null, quote: null, payload: null, pendingIssue: null, submitted: false, stopRequested: false, stopping: false, queuePaused: true, error: null, ...(Number.isFinite(remaining) ? { remaining } : {}) }));
  persist();
  if (!Number.isFinite(remaining)) void useAssistantStore.getState().refreshUsage();
}
type AssistantState = SessionData & {
  open: boolean; docked: boolean; issuesOpen: boolean; identity: string | null; editingAllowed: boolean;
  appliedEdit: AssistantAppliedEdit | null;
  phase: "idle" | "quoting" | "confirm" | "sending";
  quote: AssistantQuote | null; payload: AssistantPayload | null; error: AssistantFailure | null;
  progress: AssistantProgress | null;
  pendingIssue: Issue | null; stopping: boolean; stopRequested: boolean;
  incoming: DraftInput | null; remaining: number | null; submitted: boolean;
  usage: AssistantUsage | null; usageError: boolean; usageLoading: boolean; refreshUsage(): Promise<void>;
  openDraft(input?: DraftInput): void; close(): void; setDocked(value: boolean): void; openIssues(): void; closeIssues(): void;
  syncIdentity(): void; setEditingAllowed(value: boolean): void;
  setDraft(value: string): void; detachIssue(): void;
  resolveIncoming(replace: boolean): void; resetConversation(options?: { preserveDraft?: boolean }): void;
  resumePending(options?: { reconnect?: boolean }): Promise<void>;
  retryPending(): Promise<void>;
  review(queued?: AssistantQueuedMessage): Promise<boolean>; cancelReview(): void; send(refreshedQuote?: boolean): Promise<void>; submit(): Promise<void>;
  pauseQueue(): void; removeQueuedMessage(id: string): void; updateQueuedMessage(id: string, prompt: string): boolean;
  sendQueuedMessage(id: string): Promise<void>; steerQueuedMessage(id: string): Promise<void>; stop(): Promise<void>;
  chooseForMe(turnId: string): Promise<void>;
  chooseReply(turnId: string, choice: string): Promise<void>;
  apply(turnId: string): boolean; undoAppliedEdit(turnId: string): boolean; dismiss(turnId: string): void;
  feedback(turnId: string, feedback: AssistantFeedback): Promise<boolean>;
};
export const useAssistantStore = create<AssistantState>((set, get) => ({
  ...blank(), open: false, docked: false, issuesOpen: false, identity: null, editingAllowed: false, appliedEdit: null,
  phase: "idle", progress: null, quote: null, payload: null, submitted: false, error: null, incoming: null, remaining: null,
  pendingIssue: null, stopping: false, stopRequested: false,
  usage: null, usageError: false, usageLoading: false,
  refreshUsage: async () => {
    if (!useAuthStore.getState().ready || !aiAccountId(useAuthStore.getState().user)) {
      usageRequest++;
      set({ usage: null, remaining: null, usageError: false, usageLoading: false });
      return;
    }
    const identity = get().identity;
    const revision = balanceRevision;
    const request = ++usageRequest;
    set({ usageError: false, usageLoading: true });
    try {
      const result = await getAssistantUsage();
      if (identity !== get().identity || identity !== assistantIdentity() || request !== usageRequest) return;
      const current = get();
      const deniedBalance = current.error?.remaining ?? current.remaining;
      const creditErrorResolved = result.ok && result.usage.remaining > 0 &&
        (current.error?.reason === "credits" || current.error?.reason === "insufficient_credit_budget" &&
          deniedBalance != null && result.usage.remaining > deniedBalance);
      if (result.ok) set({ usage: result.usage, ...(revision === balanceRevision ? {
        remaining: result.usage.remaining,
        ...(creditErrorResolved ? { error: null } : {}),
      } : {}) });
      else set({ usageError: true });
    } catch { if (identity === get().identity && request === usageRequest) set({ usageError: true }); }
    finally { if (identity === get().identity && request === usageRequest) set({ usageLoading: false }); }
  },
  syncIdentity: () => {
    const identity = assistantIdentity();
    if (get().identity === identity) return;
    const old = get();
    // An explicit sign-in can retain a guest draft on the same local drawing.
    const guestResume = old.identity?.startsWith("guest:") && old.identity.slice(6) === identity.slice(identity.indexOf(":") + 1) && old.messages.length === 0;
    epoch++;
    stopTransport();
    balanceRevision++;
    usageRequest++;
    const restored = saved(identity);
    const redirectedGuest = takeGuestSignInDraft(identity);
    const guestData = guestResume ? { ...blank(), conversationId: old.conversationId, messages: [], draft: old.draft, issue: old.issue } : redirectedGuest;
    const guestHasDraft = guestData && guestData.draft.trim().length > 0;
    // A just-written guest draft wins over an old completed conversation. Keep
    // an outstanding paid request recoverable and let the user choose whether
    // to replace it with the guest draft instead.
    const pending = restored?.pending;
    const data = guestHasDraft && !pending ? guestData : restored ?? guestData ?? blank();
    const incoming = guestHasDraft && pending ? { prompt: guestData.draft, ...(guestData.issue ? { issue: guestData.issue } : {}) } : null;
    set({ ...data, identity, docked: false, appliedEdit: null, phase: "idle", progress: pending ? restored?.progress ?? null : null, quote: null, payload: pending ?? null, pendingIssue: pending ? restored?.pendingIssue ?? null : null, stopping: false, stopRequested: !!pending && restored?.stopRequested === true, submitted: !!pending, error: pending ? { ok: false, reason: "retry_same_request" } : null, incoming, remaining: null, usage: null, usageError: false, usageLoading: false, open: redirectedGuest ? true : guestHasDraft ? old.open : restored?.open ?? (pending ? true : old.open) });
    persist();
  },
  setEditingAllowed: editingAllowed => set({ editingAllowed }),
  openDraft: (input = {}) => {
    if (!useFeatureFlags.getState().flags.editorAssistant) return;
    get().syncIdentity();
    set({ docked: false });
    useSystemStore.getState().setMobileDetailsOpen(false);
    const current = get();
    const hasNewDraft = input.prompt !== undefined || input.issue !== undefined;
    if ((current.draft.trim() || current.payload || current.stopping) && hasNewDraft) {
      set({ open: true, issuesOpen: false, incoming: input });
      return;
    }
    if (hasNewDraft) {
      const mode = input.mode ?? "ask";
      const title = input.issue ? t(input.issue.title.key as TranslationKey, input.issue.title.vars) : "";
      const draft = input.prompt ?? (input.issue ? t(mode === "edit" ? input.issue.code === "custom-component-ai-estimated" ? "assistant.verify_specs_prompt" : "assistant.fix_prompt" : "assistant.ask_prompt", { title }) : "");
      epoch++;
      set({ draft, issue: input.issue ?? null, phase: "idle", progress: null, quote: null, payload: null, submitted: false, error: null });
    }
    set({ open: true, issuesOpen: false });
    persist();
  },
  close: () => { set({ open: false, docked: false, incoming: null }); persist(); },
  setDocked: docked => set({ docked }),
  openIssues: () => {
    if (!useFeatureFlags.getState().flags.editorAssistant) return;
    useSystemStore.getState().setMobileDetailsOpen(false);
    set({ issuesOpen: true, open: false, docked: false });
  },
  closeIssues: () => set({ issuesOpen: false }),
  resolveIncoming: replace => {
    const input = get().incoming;
    set({ incoming: null });
    if (!replace || !input) return;
    if (get().payload || get().stopping) {
      const title = input.issue ? t(input.issue.title.key as TranslationKey, input.issue.title.vars) : "";
      set({ draft: (input.prompt ?? t(input.mode === "edit" ? "assistant.fix_prompt" : "assistant.ask_prompt", { title })).slice(0, ASSISTANT_MAX_PROMPT_CHARS), issue: input.issue ?? null }); persist(); return;
    }
    set({ draft: "" }); get().openDraft(input);
  },
  setDraft: value => {
    get().syncIdentity();
    if (get().payload || get().stopping) { set({ draft: value.slice(0, ASSISTANT_MAX_PROMPT_CHARS) }); persist(); return; }
    epoch++;
    set({ draft: value.slice(0, ASSISTANT_MAX_PROMPT_CHARS), phase: "idle", progress: null, quote: null, payload: null, submitted: false, error: null }); persist();
  },
  detachIssue: () => { if (get().payload || get().stopping) { set({ issue: null }); persist(); return; } epoch++; set({ issue: null, quote: null, payload: null, submitted: false, phase: "idle", progress: null }); persist(); },
  resetConversation: (options = {}) => {
    if (get().payload || get().stopping) return;
    const keptDraft = options.preserveDraft ? { draft: get().draft, issue: get().issue } : {};
    epoch++;
    set({ ...blank(), ...keptDraft, docked: false, appliedEdit: null, phase: "idle", progress: null, quote: null, payload: null, pendingIssue: null, submitted: false, stopping: false, stopRequested: false, error: null });
    persist();
  },
  review: async queued => {
    get().syncIdentity();
    const state = get();
    if (!useFeatureFlags.getState().flags.editorAssistant || (!state.payload && !(queued?.prompt ?? state.draft).trim()) || state.phase === "sending" || state.phase === "quoting" || state.stopping || state.stopRequested) return false;
    const auth = useAuthStore.getState();
    if (!auth.ready) return false;
    if (!aiAccountId(auth.user)) { set({ error: { ok: false, reason: "sign_in" } }); return false; }
    if (typeof navigator !== "undefined" && !navigator.onLine) { set({ error: { ok: false, reason: "offline" } }); return false; }
    const mode = assistantMayEdit() ? "edit" : "ask";
    const editor = useSystemStore.getState();
    const simulation = snapshotAssistantSimulation(editor.system, editor.simulateState);
    const requestIssue = queued ? queued.issue : state.payload ? state.pendingIssue : state.issue;
    const retryPayload = state.payload && (state.submitted || (state.payload.mode === mode && assistantSystemFingerprint(state.payload.system) === assistantSystemFingerprint(editor.system) && state.payload.autoSizeTargetPct === useAutoSizeVdropTarget.getState().target && sameAssistantSimulation(state.payload.simulation, simulation))) ? state.payload : null;
    const payload: AssistantPayload = retryPayload ?? {
      requestId: crypto.randomUUID(), conversationId: state.conversationId, prompt: (queued?.prompt ?? state.payload?.prompt ?? state.draft).trim(), mode,
      history: state.messages.slice(-ASSISTANT_MAX_HISTORY_TURNS).map(m => ({ role: m.role, content: m.stopped ? `${m.content}\n[${m.steered ? "The user interrupted this request to update it with the next message. Keep this original intent and incorporate that update; no earlier answer or edit was applied." : "This request was stopped before its answer was applied."}]` : m.content })),
      system: editor.system, simulation, issue: requestIssue ? assistantIssueReference(requestIssue) : undefined,
      projectId: useAuthStore.getState().currentProjectId, locale: useI18n.getState().locale,
      autoSizeTargetPct: useAutoSizeVdropTarget.getState().target,
    };
    const id = ++epoch;
    set({ phase: "quoting", progress: null, error: null, quote: null, payload,
      ...(!state.payload ? { pendingIssue: requestIssue, ...(queued ? { queuedMessages: state.queuedMessages.filter(message => message.id !== queued.id) } : { draft: "", issue: null }) } : {}),
    }); persist();
    try {
      const result = await quoteAssistant(payload, () => id === epoch && get().identity === assistantIdentity());
      if (id !== epoch || get().identity !== assistantIdentity()) return false;
      balanceRevision++;
      if (!result.ok) { set({ phase: "idle", progress: null, error: result, queuePaused: true, ...(Number.isFinite(result.remaining) ? { remaining: result.remaining } : {}), ...(!state.submitted || ["failed", "expired"].includes(result.reason) ? { ...restorePendingPrompt(payload), payload: null, pendingIssue: null, submitted: false } : {}) }); persist(); return false; }
      set({ phase: "confirm", progress: null, quote: result.quote, remaining: result.quote.remaining });
      return true;
    } catch (error) {
      if (id === epoch) { set({ phase: "idle", progress: null, queuePaused: true, ...(!state.submitted ? { ...restorePendingPrompt(payload), payload: null, pendingIssue: null } : {}), error: { ok: false, reason: "unavailable",
        ...(isAssistantClientError(error) ? { connectionReason: error.reason } : {}),
      } }); persist(); }
    }
    return false;
  },
  // One explicit Send authorizes this snapshot. Later typing belongs to the
  // next message and can never change the request being quoted or sent.
  submit: async () => {
    get().syncIdentity();
    const state = get();
    if (state.payload || state.stopping || state.queuedMessages.length) {
      if (state.draft.trim() && state.queuedMessages.length < 20) {
        set({ queuedMessages: [...state.queuedMessages, { id: crypto.randomUUID(), prompt: state.draft.trim(), issue: state.issue }], draft: "", issue: null }); persist();
        if (!state.payload && !state.stopping) drainQueue(state.identity);
      } else if (!state.draft.trim() && state.submitted && state.phase !== "sending" && state.phase !== "quoting" && !state.stopping && !state.stopRequested) {
        if (await get().review()) await get().send();
      }
      return;
    }
    set({ queuePaused: false });
    if (await get().review()) await get().send();
  },
  retryPending: async () => {
    get().syncIdentity();
    const state = get();
    if (!state.submitted || !state.payload || state.phase === "sending" || state.phase === "quoting" || state.stopping || state.stopRequested) return;
    // Manual recovery may safely submit a missing original request, using its
    // exact identity. The separate composer and queue are never consumed.
    if (await get().review()) await get().send();
  },
  pauseQueue: () => { get().syncIdentity(); set({ queuePaused: true }); persist(); },
  removeQueuedMessage: id => { get().syncIdentity(); set(state => ({ queuedMessages: state.queuedMessages.filter(message => message.id !== id) })); persist(); },
  updateQueuedMessage: (id, prompt) => {
    get().syncIdentity();
    const text = prompt.trim();
    if (!text || text.length > ASSISTANT_MAX_PROMPT_CHARS || !get().queuedMessages.some(message => message.id === id)) return false;
    set(state => ({ queuedMessages: state.queuedMessages.map(message => message.id === id ? { ...message, prompt: text } : message) })); persist(); return true;
  },
  sendQueuedMessage: async id => {
    get().syncIdentity();
    const state = get();
    const queued = state.queuedMessages.find(message => message.id === id);
    if (!queued || state.payload || state.phase !== "idle" || state.stopping || state.stopRequested) return;
    set({ queuePaused: false }); persist();
    if (await get().review(queued)) await get().send();
    else if (get().identity === state.identity && state.identity === assistantIdentity() && get().queuedMessages.some(message => message.id === id)) { set({ queuePaused: true }); persist(); }
  },
  stop: async () => {
    get().syncIdentity();
    const state = get();
    if (state.stopping) return;
    set({ queuePaused: true });
    if (!state.payload) { persist(); return; }
    const payload = state.payload;
    const id = ++epoch;
    stopTransport();
    if (!state.submitted) { receiveStopped(payload); return; }
    set({ stopping: true, stopRequested: true, progress: null, error: null }); persist();
    const current = () => id === epoch && get().identity === state.identity && state.identity === assistantIdentity() && get().payload?.requestId === payload.requestId;
    try {
      const result = await cancelAssistant(payload, current);
      if (!current()) return;
      balanceRevision++;
      if (result.ok) { receiveStopped(payload, result.remaining, result.charged); return; }
      if (result.reason === "cancelled") { receiveStopped(payload, result.remaining); return; }
      set({ stopping: false, phase: "idle", error: { ...result, reason: "cancel_failed" } }); persist();
    } catch (error) {
      if (current()) { set({ stopping: false, phase: "idle", error: { ok: false, reason: "cancel_failed", ...(isAssistantClientError(error) ? { connectionReason: error.reason } : {}) } }); persist(); }
    }
  },
  steerQueuedMessage: async id => {
    get().syncIdentity();
    const state = get();
    if (state.stopping || !state.queuedMessages.some(message => message.id === id)) return;
    const identity = state.identity;
    const original = state.payload;
    await get().stop();
    if (get().identity !== identity || identity !== assistantIdentity() || get().stopRequested || get().payload) return;
    if (original) {
      set(current => ({ messages: current.messages.map(turn => turn.id === `${original.requestId}:user` ? { ...turn, steered: true } : turn), queuedMessages: current.queuedMessages.map(message => message.id === id && !message.issue && state.pendingIssue ? { ...message, issue: state.pendingIssue } : message) })); persist();
    }
    await get().sendQueuedMessage(id);
  },
  chooseForMe: async turnId => {
    if (!assistantCanChooseForMe(turnId)) return;
    // Seed synchronously before quoting, so a second tap cannot send again.
    // The normal flow keeps history, current permissions and credit recovery.
    get().setDraft(t("assistant.choose_for_me_prompt"));
    await get().submit();
  },
  chooseReply: async (turnId, choice) => {
    if (!assistantCanReplyTo(turnId) || !get().messages.at(-1)?.reply?.choices?.includes(choice)) return;
    get().setDraft(choice);
    await get().submit();
  },
  cancelReview: () => { if (get().submitted) return; const payload = get().payload; epoch++; set({ ...(payload ? restorePendingPrompt(payload) : {}), phase: "idle", progress: null, quote: null, payload: null, pendingIssue: null, submitted: false, error: null, queuePaused: true }); persist(); },
  send: async (refreshedQuote = false) => {
    const state = get();
    if (state.phase !== "confirm" || !state.quote || !state.payload || state.stopping || state.stopRequested || !useFeatureFlags.getState().flags.editorAssistant) return;
    const { payload, quote } = state;
    if (Date.parse(quote.expiresAt) <= Date.now()) {
      // The explicit Send already authorized this exact snapshot. Refresh once
      // and continue; a repeatedly expired server quote must not loop or leave
      // an empty composer with an unsubmitted, unreachable request.
      if (refreshedQuote) {
        set({ phase: "idle", quote: null, progress: null, queuePaused: true, error: { ok: false, reason: "quote_expired" }, ...(!state.submitted ? { ...restorePendingPrompt(payload), payload: null, pendingIssue: null } : {}) }); persist(); return;
      }
      if (await get().review() && get().identity === state.identity && get().payload?.requestId === payload.requestId) await get().send(true);
      return;
    }
    if (state.identity !== assistantIdentity()) { get().syncIdentity(); return; }
    const capabilityChanged = payload.mode !== (assistantMayEdit() ? "edit" : "ask");
    if (!quote.recovery && (capabilityChanged || assistantSystemFingerprint(payload.system) !== assistantSystemFingerprint(useSystemStore.getState().system) ||
        payload.autoSizeTargetPct !== useAutoSizeVdropTarget.getState().target ||
        !sameAssistantSimulation(payload.simulation, snapshotAssistantSimulation(useSystemStore.getState().system, useSystemStore.getState().simulateState)))) {
      // Capability is part of the authorized snapshot. Preserve an uncertain
      // submitted request until the server can confirm whether it is recoverable.
      set({ phase: "idle", progress: null, quote: null, queuePaused: true, ...(state.submitted ? {} : { ...restorePendingPrompt(payload), payload: null, pendingIssue: null }), error: { ok: false, reason: capabilityChanged && payload.mode === "edit" ? "readonly" : "request_changed" } }); persist(); return;
    }
    const id = ++epoch;
    const signal = startTransport(id, false);
    set({ phase: "sending", progress: null, submitted: true, error: null }); persist();
    const isCurrentRequest = () => {
      const current = get();
      return id === epoch && current.phase === "sending" && current.payload?.requestId === payload.requestId &&
        current.identity === state.identity && current.identity === assistantIdentity() && useFeatureFlags.getState().flags.editorAssistant;
    };
    try {
      const result = await sendAssistant(payload, quote.token, progress => {
        if (!isCurrentRequest()) return;
        set({ progress }); persist();
      }, isCurrentRequest, signal);
      if (id !== epoch || get().identity !== assistantIdentity()) return;
      balanceRevision++;
      if (!result.ok) {
        if (result.reason === "cancelled") { receiveStopped(payload, result.remaining); return; }
        const retryable = ["in_progress", "unavailable", "retry_same_request"].includes(result.reason);
        const pending = retryable || result.reason === "quote_expired";
        set({ phase: retryable ? "confirm" : "idle", progress: null, quote: retryable ? quote : null, ...(!pending ? { ...restorePendingPrompt(payload), pendingIssue: null } : {}), payload: pending ? payload : null, submitted: pending, queuePaused: true, error: result, ...(Number.isFinite(result.remaining) ? { remaining: result.remaining } : {}) }); persist(); return;
      }
      receiveAssistantReply(payload, result);
    } catch (error) {
      // Keep the SAME request identity: a lost response may already be billed.
      if (id === epoch) { set({ phase: "confirm", progress: null, queuePaused: true, error: { ok: false, reason: "retry_same_request",
        ...(isAssistantClientError(error) ? { connectionReason: error.reason } : {}),
      } }); persist(); }
    } finally { stopTransport(id); }
  },
  resumePending: async (options = {}) => {
    const auth = useAuthStore.getState();
    if (!auth.ready || !aiAccountId(auth.user) || !useProjectStore.getState().initialized ||
        !useFeatureFlags.getState().flags.editorAssistant || (typeof navigator !== "undefined" && !navigator.onLine)) return;
    get().syncIdentity();
    const state = get();
    if (state.stopRequested) { await get().stop(); return; }
    if (!state.submitted) { drainQueue(state.identity); return; }
    if (!state.submitted || !state.payload || state.phase === "quoting" ||
        (state.phase === "sending" && activeTransport && (!options.reconnect || (activeTransport.recovery && Date.now() - activeTransport.startedAt < 1_000)))) return;
    const { payload } = state;
    const id = ++epoch;
    const signal = startTransport(id, true);
    set({ phase: "sending", progress: { phase: "recovering" }, error: null }); persist();
    const current = () => id === epoch && get().payload?.requestId === payload.requestId &&
      get().identity === state.identity && get().identity === assistantIdentity() && useFeatureFlags.getState().flags.editorAssistant;
    try {
      const result = await resumeAssistant(payload, progress => {
        if (current()) { set({ progress }); persist(); }
      }, current, signal);
      if (!current()) return;
      balanceRevision++;
      if (result.ok) { receiveAssistantReply(payload, result); return; }
      if (result.reason === "cancelled") { receiveStopped(payload, result.remaining); return; }
      const terminal = result.reason === "failed" || result.reason === "expired";
      set({ phase: "idle", progress: null, quote: null, error: result, queuePaused: true,
        ...(terminal ? { ...restorePendingPrompt(payload), payload: null, pendingIssue: null, submitted: false } : {}),
        ...(Number.isFinite(result.remaining) ? { remaining: result.remaining } : {}),
      }); persist();
    } catch (error) {
      if (current()) {
        set({ phase: "idle", progress: null, quote: null, queuePaused: true, error: { ok: false, reason: "retry_same_request",
          ...(isAssistantClientError(error) ? { connectionReason: error.reason } : {}),
        } }); persist();
      }
    } finally { stopTransport(id); }
  },
  apply: turnId => {
    if (get().identity !== assistantIdentity()) return false;
    const turn = get().messages.find(m => m.id === turnId);
    if (!turn?.reply?.proposal || turn.applied || turn.dismissed) return false;
    const fail = (reason: "stale" | "readonly" | "failed") => {
      // The reply already succeeded and may have used credits. A local apply
      // failure belongs to that turn, never to the request/refund error banner.
      set(s => ({ messages: s.messages.map(m => m.id === turnId ? { ...m, applicationError: reason } : m) }));
      persist();
      return false;
    };
    if (!useFeatureFlags.getState().flags.editorAssistant || !assistantMayEdit()) return fail("readonly");
    const before = useSystemStore.getState().system;
    const prepared = prepareAssistantProposal(before, turn.reply.proposal, { autoSizeTargetPct: useAutoSizeVdropTarget.getState().target });
    if (!prepared.ok) return fail(assistantSystemFingerprint(before) !== turn.reply.proposal.baseFingerprint ||
      (turn.reply.proposal.autoSizeTargetPct ?? 3) !== useAutoSizeVdropTarget.getState().target ? "stale" : "failed");
    const applied = useSystemStore.getState().applyAiSystemEdit(prepared.system, { nodeIds: prepared.addedNodeIds, cableIds: prepared.addedCableIds });
    if (!applied) return fail("failed");
    const editor = useSystemStore.getState();
    const appliedEdit: AssistantAppliedEdit = { id: turnId, identity: get().identity!, before, after: editor.system };
    const undoSnapshot = editor.past[editor.past.length - 1];
    if (undoSnapshot) appliedUndoSnapshots.set(appliedEdit, undoSnapshot);
    set(s => ({ appliedEdit, messages: s.messages.map(m => m.id === turnId ? { ...m, applied: true, applicationError: undefined } : m) })); persist();
    void get().feedback(turnId, "applied"); return true;
  },
  undoAppliedEdit: turnId => {
    if (!assistantCanUndoAppliedEdit(turnId)) return false;
    const beforeUndo = useSystemStore.getState().system;
    useSystemStore.getState().undo();
    if (useSystemStore.getState().system === beforeUndo) return false;
    // Keep the transient event so both native and assistant Undo/Redo derive
    // their current status from the drawing instead of persisting a stale flag.
    return true;
  },
  dismiss: turnId => { set(s => ({ messages: s.messages.map(m => m.id === turnId ? { ...m, dismissed: true } : m) })); persist(); void get().feedback(turnId, "dismissed"); },
  feedback: async (turnId, feedback) => {
    if (get().identity !== assistantIdentity()) return false;
    const identity = get().identity;
    const turn = get().messages.find(m => m.id === turnId);
    if (!turn) return false;
    try {
      const ok = await sendAssistantFeedback(turn.requestId, feedback);
      if (ok && get().identity === identity && identity === assistantIdentity()) { set(s => ({ messages: s.messages.map(m => m.id === turnId ? { ...m, feedback } : m) })); persist(); }
      return ok;
    } catch { return false; }
  },
}));
